import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, List, ListItem, SingleSelectDropdown, StackingRootContext, TextFieldWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { IIdentityPool } from '~src/authentication/serverApi/types/identityPools/IIdentityPool';
import { IdentityPoolService } from '~src/authentication/services/IdentityPoolService';
import { SessionServerSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { useSelector } from '~src/settings/general-settings/utils';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { GetSiteSAMLConfigurationRequest } from '~src/settings/siteAuthSettings/serverApi/requests/GetSiteSAMLConfigurationRequest';
import { ISiteSAMLConfiguration } from '~src/settings/siteAuthSettings/serverApi/types/ISiteSAMLConfiguration';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { scimSettingsStyles } from './ScimSettingsStyles';
import { IScimConfig, ScimConfigService } from './service/ScimConfigService';

export const SCIM_BASE_URL_PREFIX = `https://${window.location.hostname}/vizportal/api/server/sites/`;
export const SCIM_BASE_URL_SUFFIX = '/scim/v2/';
const AUTH_TYPES = {
  SERVER_DEFAULT: 'ServerDefault',
  SAML: 'Saml',
  IDENTITY_POOL: 'IdentityPool',
  DELETED_IDENTITY_POOL: 'DeletedIdentityPool'
} as const;

const generateScimBaseUrl = (): string => {
  return `${SCIM_BASE_URL_PREFIX}${getServerSessionSiteLuid()}${SCIM_BASE_URL_SUFFIX}`;
};

export function ScimSiteSettingsSection(): JSX.Element {
  const [isSamlEnabled, setIsSamlEnabled] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [secret, setSecret] = React.useState<string | null>(null);
  const [secretExists, setSecretExists] = React.useState(false);
  const [scimProvisioningEnabled, setScimProvisioningEnabled] = React.useState(false);
  const [existingScimConfig, setExistingScimConfig] = React.useState<IScimConfig | null>(null);
  const [showIdentityPoolUnavailableWarning, setShowIdentityPoolUnavailableWarning] = React.useState(false);
  const [authTypesDropdownListItems, setAuthTypesDropdownListItems] = React.useState<ListItem[]>([]);
  const [selectedAuthType, setSelectedAuthType] = React.useState<ListItem | null>(null);
  const [identityPools, setIdentityPools] = React.useState<IIdentityPool[]>([]);
  const generateSecretButtonHandler = useGenerateSecretButtonHandler(setSecret, setGenerating, setSecretExists, secretExists, existingScimConfig?.id);

  React.useEffect(() => {
    const sessionService = getSessionManagementServiceInstance();
    sessionService.bootstrapSessionPromise.then(() => {
      WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest())
        .then((samlConfiguration: ISiteSAMLConfiguration) => {
          setIsSamlEnabled(samlConfiguration.enabled);
        });
    });
    // If configuration is found, set `scimProvisioningEnabled` to true
    // If a secret exists within the config, also set `secretExists` to true
    if (FeatureFlags.scimSiteSettingsEnabled) {
      ScimConfigService.getScimConfig()
        .then((result: IScimConfig) => {
          setScimProvisioningEnabled(true);
          setExistingScimConfig(result);
          if (result.apiTokenId) {
            setSecretExists(true);
          }
        })
        .catch(() => {
          // keep default states
        });

    }
    // Make Identity Pools call to fetch active Identity Pools
    if (FeatureFlags.authAgnosticScimOnServerEnabled && ServerSettingsUnauthenticated.isIdentityPoolsEnabled()) {
      IdentityPoolService.tryFetchActiveIdentityPools()
        .then((result: IIdentityPool[]) => {
          setIdentityPools(result || []);
        })
        .catch(() => setIdentityPools([]));
    } else {
      setIdentityPools([]);
    }
  }, []);

  React.useEffect(() => {
    if (!FeatureFlags.authAgnosticScimOnServerEnabled) {
      setAuthTypesDropdownListItems([]);
      return;
    }
    // Initialize dropdown elements
    const items: ListItem[] = [
      {
        content: <div>{Messages.serverDefaultDropdownOption()}</div>,
        testId: 'dropdown-element-serverdefault',
        value: AUTH_TYPES.SERVER_DEFAULT
      },
    ];
    if (isSamlEnabled) {
      items.push({
        content: <div>{Messages.siteSamlDropdownOption()}</div>,
        testId: 'dropdown-element-sitesaml',
        value: AUTH_TYPES.SAML
      });
    }
    // For identity Pools - preceed Identity Pool name with OIDC
    if (identityPools?.length) {
      identityPools
        .forEach((pool: IIdentityPool) => {
          items.push({
            content: <div>{Messages.identityPoolNamesDropdownOption({ POOL_NAME: pool.name })}</div>,
            testId: `dropdown-element-oidc-${pool.id}`,
            value: `${pool.id}`
          });
        });
    }
    setAuthTypesDropdownListItems(items);
    if ((existingScimConfig?.authentication || existingScimConfig?.authenticationId)) {
      let existingScimConfigAuth: string | undefined;

      // Set Identity Pool Id as current Auth in case of identity pool otherwise assign whatever is the authentication

      if (existingScimConfig?.authentication === AUTH_TYPES.IDENTITY_POOL && existingScimConfig?.authenticationId) {
        existingScimConfigAuth = existingScimConfig.authenticationId;
      } else {
        existingScimConfigAuth = existingScimConfig.authentication;
      }

      const existingAuth = items.find((i) => i.value === existingScimConfigAuth);
      if (existingAuth) {
        setSelectedAuthType(existingAuth);
        setShowIdentityPoolUnavailableWarning(false);
      }
      // if auth is not found means it is deleted/disabled identity pool.
      else if (existingScimConfig?.authentication === AUTH_TYPES.IDENTITY_POOL && identityPools) {
        setSelectedAuthType({
          content: <div>{Messages.identityPoolNotAvailableDropdownOption()}</div>,
          testId: 'dropdown-element-unavailable',
          // Assign auth Id itself for disabled and identity Pool deleted constant in case of deletion
          value: existingScimConfig.authenticationId ? existingScimConfig.authenticationId : AUTH_TYPES.DELETED_IDENTITY_POOL
        });
        // Warning to show that selected identity pool is disabled/deleted.
        setShowIdentityPoolUnavailableWarning(true);
      }
    }
  }, [isSamlEnabled, identityPools, existingScimConfig]);

  const authChanged = React.useMemo(() => {
    // To determine if auth is changed (To display Save & Cancel buttons)
    const existingScimConfigAuth = existingScimConfig?.authentication as string | undefined;
    const existingScimConfigAuthId = existingScimConfig?.authenticationId as string | undefined;
    const selectedAuthInDropdown = selectedAuthType?.value as string | undefined;

    // Assigning the value to compare with the selected Dropdown Option
    // For serverDefault & Saml it will be authentication itself.
    // For identity Pool (active) it will be auth Id (identity pool id basically).
    // For disabled identity Pool --  auth Id will be present, assign that.
    // For deleted identity pool auth Id will be null
    let currentConfigAuth: string | undefined;
    if (existingScimConfigAuth === AUTH_TYPES.IDENTITY_POOL) {
      if (existingScimConfigAuthId != null) {
        currentConfigAuth = existingScimConfigAuthId;
        if (showIdentityPoolUnavailableWarning && currentConfigAuth !== selectedAuthInDropdown)
          setShowIdentityPoolUnavailableWarning(false);
      }
      else {
        if (selectedAuthInDropdown === AUTH_TYPES.DELETED_IDENTITY_POOL)
          return false;
        setShowIdentityPoolUnavailableWarning(false);
        return true;
      }
    }

    else
      currentConfigAuth = existingScimConfigAuth;

    return Boolean(currentConfigAuth && selectedAuthInDropdown && currentConfigAuth !== selectedAuthInDropdown);
  }, [existingScimConfig, selectedAuthType, showIdentityPoolUnavailableWarning]);


  if (!useShowSettingsSection()) {
    return null;
  }

  const toggleScimProvisioning = (checked: boolean) => {

    if (checked) {

      if (existingScimConfig != null) {
        // do a update call if config exists
        const scimConfig: IScimConfig = {
          isEnabled: true,
          id: existingScimConfig.id
        };
        ScimConfigService.updateScimConfig(scimConfig)
          .then((result: IScimConfig) => {
            setScimProvisioningEnabled(true);
            setExistingScimConfig(result);
          })
          .catch(() => {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: Messages.enableScimError()
            });
          });

      }
      else {
        // do a post call if scim config doesn't exist
        const scimConfig: IScimConfig = {
          mode: 'SiteLevel',
          siteIds: [getServerSessionSiteLuid()],
        };
        ScimConfigService.createScimConfig(scimConfig)
          .then((result: IScimConfig) => {
            setScimProvisioningEnabled(true);
            setExistingScimConfig(result);
            if (FeatureFlags.authAgnosticScimOnServerEnabled) {
            const serverDefaultOption = authTypesDropdownListItems.find((i) => i.value === AUTH_TYPES.SERVER_DEFAULT);
            if (serverDefaultOption) {
              setSelectedAuthType(serverDefaultOption);
            }
          }
          })
          .catch(() => {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: Messages.enableScimError()
            });
          });
      }
    }

    if (!checked) {
      // do a update call to disable scim
      const scimConfig: IScimConfig = {
        isEnabled: false,
        id: existingScimConfig.id
      };
      ScimConfigService.updateScimConfig(scimConfig)
        .then((result: IScimConfig) => {
          setScimProvisioningEnabled(false);
          setExistingScimConfig(result);
          setSecret(null);
          setSecretExists(false);
        })
        .catch(() => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.disableScimError()
          });
        });
    }
  };

  const selectAuthType = (id: string) => {
    setSelectedAuthType(
      authTypesDropdownListItems.find((item: ListItem) => item.value === id) || null
    );
  };

  const handleAuthenticationSave = () => {
    const selectedAuth = selectedAuthType?.value as string | undefined;
    if (!selectedAuth) return;
    let scimConfig: IScimConfig;
    if(selectedAuth=== AUTH_TYPES.SAML || selectedAuth===AUTH_TYPES.SERVER_DEFAULT) {
        scimConfig = {
        id: existingScimConfig.id,
        authentication: selectedAuth
      };
    }
    else {
        scimConfig = {
        id: existingScimConfig.id,
        authentication: AUTH_TYPES.IDENTITY_POOL,
        authenticationId: selectedAuth
      };
    }
    const showSecretInvalidateToast = secretExists;
    ScimConfigService.updateScimConfig(scimConfig)
      .then((result: IScimConfig) => {
        if (showSecretInvalidateToast) {
          ToasterService.instance.pop({
            type: ToastType.Warning,
            children: renderWarning()
          });
        }
        setTimeout(() => {
          ToasterService.instance.pop({
            type: ToastType.Success,
            text: Messages.settingsUpdateSuccess()
          });
        }, 100);
        setExistingScimConfig(result);
        setSecret(null);
        setSecretExists(false);
        setShowIdentityPoolUnavailableWarning(false);
      })
      .catch(() => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.authenticationTypeUpdateError()
        });
      });
  };

  const shouldShowSecretWarning = () => {
    return FeatureFlags.authAgnosticScimOnServerEnabled &&
      !showIdentityPoolUnavailableWarning &&
      secretExists &&
      authChanged;
  };

  const handleAuthenticationCancel = () => {
    let currentScimConfigAuth: string | undefined;
    // For defaulting to previous selected auth.
    if (existingScimConfig?.authentication === AUTH_TYPES.IDENTITY_POOL) {
      if (existingScimConfig?.authenticationId)
        currentScimConfigAuth = existingScimConfig.authenticationId;
      else
        currentScimConfigAuth = AUTH_TYPES.DELETED_IDENTITY_POOL;
    } else {
      currentScimConfigAuth = existingScimConfig?.authentication;
    }

    const match = authTypesDropdownListItems.find((i) => i.value === currentScimConfigAuth);
    if (match) {
      setSelectedAuthType(match);
    } else {
      setSelectedAuthType({
        content: <div>{Messages.identityPoolNotAvailableDropdownOption()}</div>,
        testId: 'dropdown-element-unavailable',
        value: currentScimConfigAuth
      });
      setShowIdentityPoolUnavailableWarning(true);
    }
  };


  return <SettingsSection title={Messages.siteScimConfigurationsHeading()} description={renderDescription()}>
    <CheckBoxWithLabelWidget
      label={Messages.scimSettingsEnable()}
      checked={scimProvisioningEnabled}
      testId={'scim-settings-checkbox'}
      disabled={!FeatureFlags.authAgnosticScimOnServerEnabled && !isSamlEnabled}
      handleChange={toggleScimProvisioning}
    />
    {scimProvisioningEnabled && (
      <div style={scimSettingsStyles.baseUrlContainer}>
        <TextFieldWidget
          label={Messages.scimBaseUrl()}
          testId='scim-settings-baseUrl'
          style={scimSettingsStyles.baseUrlField}
          value={generateScimBaseUrl()}
          onClick={selectAll}
          readOnly
        />
        <div>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={'scim-settings-generate-secret-button'}
            className={scimSettingsStyles.secretButton}
            type={'button'}
            disabled={generating}
            onClick={generateSecretButtonHandler}
          >
            {Messages.scimConfigurationGenerateNewSecret()}
          </ButtonWidget>
          {/* secret will be shown only once */}
          {secret && (
            <div className={scimSettingsStyles.secretContainer}>
              <TextFieldWidget
                kind='line'
                label={Messages.personalAccessTokenSecret()}
                testId={'scim-settings-secret'}
                value={secret}
                style={scimSettingsStyles.secretField}
                onClick={selectAll}
                readOnly
              />
              <CompactInlineAlertWidget type={InlineAlertType.Info} testId="scim-settings-secret-info" style={scimSettingsStyles.secretInfo}>
                {<div>{Messages.scimSecretInfo()}</div>}
              </CompactInlineAlertWidget>
            </div>
          )}
          {shouldShowSecretWarning() && (
                <div style={{ marginTop: `${CommonStyles.Sizes.SmallSpace}px` }}>
                  <CompactInlineAlertWidget type={InlineAlertType.Warning} testId="scim-settings-secret-warning" style={scimSettingsStyles.secretInfo}>
                    {<div>{Messages.scimSecretWarning()}</div>}
                  </CompactInlineAlertWidget>
                </div>
          )}
          {FeatureFlags.authAgnosticScimOnServerEnabled && (
            <div style={{ marginTop: `${CommonStyles.Sizes.MediumSpace}px` }}>
              <StackingRootContext.Provider value={null}>
                <SingleSelectDropdown
                  list={<List items={authTypesDropdownListItems}/>}
                  onChange={selectAuthType}
                  value={selectedAuthType?.value}
                  label={Messages.authentication()}
                  style={scimSettingsStyles.baseUrlField}
                  type={DropdownButtonType.Outline}
                  testId='auth-type-dropdown'
                >
                  {selectedAuthType ? selectedAuthType.content : Messages.selectAuthentication()}
                </SingleSelectDropdown>
              </StackingRootContext.Provider>
              {showIdentityPoolUnavailableWarning && (
                <div style={{marginTop: '5px'}}>
                <CompactInlineAlertWidget
                  type={InlineAlertType.Warning}
                  testId="scim-settings-identity-pool-warning"
                  style={scimSettingsStyles.secretInfo}
                >
                  <div>{Messages.identityPoolNotAvailableWarning()}</div>
                </CompactInlineAlertWidget>
                </div>
              )}
              {authChanged && (
                <div style={{ marginTop: '16px' }}>
                  <span style={{ marginRight: '8px' }}>
                    <ButtonWidget
                      buttonType={ButtonType.Outline}
                      testId={'scim-auth-cancel-button'}
                      onClick={handleAuthenticationCancel}
                    >
                      {Messages.cancel()}
                    </ButtonWidget>
                  </span>
                  <span>
                    <ButtonWidget
                      buttonType={ButtonType.Primary}
                      testId={'scim-auth-save-button'}
                      onClick={handleAuthenticationSave}
                    >
                      {Messages.save()}
                    </ButtonWidget>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}
  </SettingsSection>;
}

function selectAll(event: any): void {
  event.target.select();
}

function useGenerateSecretButtonHandler(
  setSecret: (secret: string | null) => void,
  setGenerating: (loading: boolean) => void,
  setSecretExists: (exist: boolean) => void,
  secretExists: boolean | null,
  scimConfigId: string | null
) {
  return React.useCallback(() => {
    const generateSecret = async () => {
      setGenerating(true);
      try {
        createScimApiToken(setSecret, setSecretExists, scimConfigId);
      } catch (error) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.scimSettingsGenerateSecretError()
        });
      } finally {
        setGenerating(false);
      }
    };

    if (secretExists) {
      openReactConfirmationDialog<void, any>({
        title: Messages.generateScimSecret(),
        message: Messages.scimGenerateSecretDialogMessage(),
        paramsForAction: undefined,
        action: generateSecret,
        actionName: Messages.generateScimSecret(),
        isActionDelete: false,
        dialogWidth: TabDialogWidth.width375px
      });
    } else {
      generateSecret();
    }
  }, [secretExists, setSecret, setGenerating, setSecretExists, scimConfigId]);
}

function getServerSessionSiteLuid(): string {
  return getSessionManagementServiceInstance().sessionSiteLuid();
}

function useShowSettingsSection(): boolean {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  return FeatureFlags.scimSiteSettingsEnabled && (isServerAdmin && !isOnlineEnvironment);
}

function renderDescription() {
  return (
    <>
      <span>{Messages.siteScimConfigurationsDesc()}</span>
      <TextLinkWidget
        testId={'scim-settings-learn-more'}
        className={learnMoreStyle}
        href={HelpService.getHelpUrlByCategory('scim-site')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </>
  );
}

function renderWarning() {
  return (
    <>
      <span>{Messages.scimSecretInvalidateWarning()}</span>
      <TextLinkWidget
        testId={'scim-settings-secret-invalidate-warning'}
        className={learnMoreStyle}
        href={HelpService.getHelpUrlByCategory('scim-toast-auth-change')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </>
  );
}

export function createScimApiToken(setSecret: (token: string | null) => void, setSecretExists: (exist: boolean) => void, scimConfigId: string): void {

  setSecret(null);
  setSecretExists(false);
  ScimConfigService.createApiToken(scimConfigId)
    .then((result: string) => {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: Messages.scimSettingsGenerateSecretSuccess()
      });
      setSecret(result);
      setSecretExists(true);
    })
    .catch(() => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.scimSettingsGenerateSecretError()
      });
    });
}


export const ScimSettingsSpecs = componentRegistryEntry({
  name: 'ScimSiteSettingsSection',
  component: ScimSiteSettingsSection,
});